import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { cva, VariantProps } from 'class-variance-authority'
import { ArrowLeft } from 'lucide-react'
import { Slot } from '@radix-ui/react-slot'

import { useWallet } from '@/hooks/useWallet'
import useSolana from '@/lib/solana/hooks/useSolana'
import { cn } from '@/lib/utils'
import { RoutePath } from '@/routes'

import { ArrowDown } from '../svgr'
import { Back, Iconloading, Refresh } from '../svgr/common'
import { FormControl, FormItem, FormLabel } from '../ui/form'
import { Label } from '../ui/label'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'

import { Flex } from './Box'

const radioGroupVariants = cva(
  'flex rounded-sm overflow-hidden transition [&>.item]:flex [&>.item:hover]:text-primary [&>.item[data-selected=true]]:text-white [&>.item[data-selected=true]]:bg-primary',
  {
    variants: {
      variant: {
        default: cn(
          'gap-0 bg-secondary border',
          '[&>.item]:flex-1 [&>.item]:border-r [&>.item:last-child]:border-none',
          '[&>.item]:h-8 xl:[&>.item]:h-12',
          '[&_label]:text-xs xl:[&_label]:text-base'
        ),
        secondary: cn(
          'bg-secondary px-2 py-1 border',
          '[&>.item]:rounded-sm [&>.item]:p-1 xl:[&>.item]:px-2',
          '[&_label]:text-xs'
        ),
        tertiary: cn(
          'gap-3 xl:gap-6',
          '[&>.item]:flex-1 [&>.item]:border [&>.item]:h-12 [&>.item]:bg-secondary [&>.item[data-selected=true]]:border-primary',
          '[&_label]:text-sm xl:[&_label]:text-lg'
        )
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
)

export const ButtonRadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadioGroup>,
  React.ComponentProps<typeof RadioGroup> &
    VariantProps<typeof radioGroupVariants> & {
      inForm?: boolean
      options: {
        label: string
        value: string
      }[]
    }
>(({ className, variant, options, value, inForm, ...rest }, ref) => {
  const renderOption = useCallback(
    (option: { label: string; value: string }) => {
      const WrapperComp = inForm ? FormItem : Flex
      const LabelComp = inForm ? (
        <FormLabel className="flex h-full w-full cursor-pointer items-center justify-center">
          {option.label}
        </FormLabel>
      ) : (
        <Label
          htmlFor={option.value}
          className="flex h-full w-full cursor-pointer items-center justify-center"
        >
          {option.label}
        </Label>
      )
      const RadioItem = inForm ? (
        <FormControl>
          <RadioGroupItem value={option.value} className="hidden" />
        </FormControl>
      ) : (
        <RadioGroupItem
          value={option.value}
          id={option.value}
          className="hidden"
        />
      )

      return (
        <WrapperComp
          key={option.value}
          data-selected={value === option.value}
          className="item font-Orbitron"
        >
          {RadioItem}
          {LabelComp}
        </WrapperComp>
      )
    },
    [inForm, value]
  )

  return (
    <RadioGroup
      ref={ref}
      value={value}
      className={cn(radioGroupVariants({ variant, className }))}
      {...rest}
    >
      {options.map((option) => renderOption(option))}
    </RadioGroup>
  )
})
ButtonRadioGroup.displayName = 'ButtonRadioGroup'

const buttonVariants = cva(
  cn(
    'flex items-center justify-center gap-2 shadow',
    'rounded-sm transition-all [&_svg]:size-4'
  ),
  {
    variants: {
      variant: {
        primary: cn(
          'bg-primary hover:bg-primary-hover focus:bg-primary-pressed disabled:bg-primary-disabled disabled:text-white',
          'text-white'
        ),
        secondary: cn(
          'bg-secondary hover:bg-secondary/80 focus:bg-secondary/60 disabled:bg-secondary/40',
          'text-secondary-foreground'
        ),
        pumpfun: cn(
          'bg-pumpfun hover:bg-pumpfun/80 focus:bg-pumpfun/60 disabled:bg-pumpfun/40',
          'text-white'
        ),
        raydium: cn(
          'bg-raydium hover:bg-raydium/80 focus:bg-raydium/60 disabled:bg-raydium/40',
          'text-white'
        ),
        destructive: cn(
          'bg-destructive hover:bg-destructive-hover focus:bg-destructive-pressed disabled:bg-destructive-disabled',
          'text-destructive-foreground'
        ),
        success: cn(
          'bg-success hover:bg-success-hover focus:bg-success-pressed disabled:bg-success-disabled',
          'text-white'
        ),
        warning: cn(
          'bg-warning hover:bg-warning-hover focus:bg-warning-pressed disabled:bg-warning-disabled',
          'text-warning-foreground'
        ),
        revert: ''
      },
      size: {
        xxxs: cn(
          'h-5 xl:h-7',
          'text-xs xl:text-base',
          '[&_svg]:size-2 xl:[&_svg]:size-3',
          'px-1'
        ),
        xxs: cn('h-6 xl:h-7', 'text-sm', 'px-1'),
        xs: cn('h-6 xl:h-8', 'text-sm xl:text-base', 'px-2'),
        sm: cn('h-9 xl:h-12', 'text-base xl:text-lg', 'px-2 xl:px-4'),
        md: cn('h-12 xl:h-14', 'text-sm xl:text-lg', 'px-2 xl:px-4'),
        default: cn('h-12 xl:h-16', 'text-base xl:text-2xl', 'px-2 xl:px-4'),
        lg: cn('h-18', 'text-xl', 'px-6')
      },
      border: {
        true: 'border border-border'
      },
      outline: {
        true: 'bg-background text-primary-foreground hover:text-white'
      },
      ghost: {
        true: 'border-none bg-transparent'
      },
      link: {
        true: cn(
          'h-auto xl:h-auto px-0 bg-transparent border-none shadow-none',
          'hover:bg-transparent focus:bg-transparent disabled:bg-transparent',
          'hover:underline underline-offset-4'
        )
      }
    },
    compoundVariants: [
      {
        link: true,
        variant: 'primary',
        className: 'text-primary'
      },
      {
        link: true,
        variant: 'secondary',
        className: 'text-secondary'
      },
      {
        link: true,
        variant: 'destructive',
        className: 'text-destructive'
      },
      {
        link: true,
        variant: 'success',
        className: 'text-success'
      },
      {
        link: true,
        variant: 'warning',
        className: 'text-warning'
      }
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      border: true,
      outline: false,
      ghost: false,
      link: false
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      asChild = false,
      variant,
      size,
      border,
      outline,
      ghost,
      link,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            border,
            outline,
            ghost,
            link,
            className
          })
        )}
        ref={ref}
        type="button"
        {...props}
      />
    )
  }
)

export const SecondaryButton: React.FC<React.ComponentProps<typeof Button>> =
  React.forwardRef(({ ...props }, ref) => (
    <Button ref={ref} variant="secondary" {...props} />
  ))
SecondaryButton.displayName = 'SecondaryButton'

export const OutlineButton: React.FC<React.ComponentProps<typeof Button>> =
  React.forwardRef(({ className, ...props }, ref) => (
    <Button ref={ref} outline size="sm" className={cn(className)} {...props} />
  ))
OutlineButton.displayName = 'OutlineButton'

export const SelectButton: React.FC<
  React.ComponentProps<typeof Button> & {
    open?: boolean
  }
> = React.forwardRef(({ className, children, open, ...rest }, ref) => (
  <Button
    className={cn(['[&_svg]:size-3 xl:[&_svg]:size-4', className])}
    size="sm"
    ref={ref}
    {...rest}
  >
    {children}
    <ArrowDown
      className={cn('transition-transform', open ? 'rotate-180' : 'rotate-0')}
    />
  </Button>
))
SelectButton.displayName = 'SelectButton'

export const TagButton: React.FC<React.ComponentProps<typeof Button>> =
  React.forwardRef(({ className, children, ...rest }, ref) => (
    <Button
      ref={ref}
      size="xxxs"
      border={false}
      className={cn('bg-primary-pressed', className)}
      {...rest}
    >
      {children}
    </Button>
  ))
TagButton.displayName = 'TagButton'

export const BackButton: React.FC<
  React.ComponentProps<typeof Button> & { single?: boolean }
> = React.forwardRef(
  ({ className, children, single = false, onClick, ...rest }, ref) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const handleClick = useCallback(
      (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (window.history.state.idx) {
          navigate(-1)
        } else {
          navigate(RoutePath.HOME)
        }
        onClick?.(ev)
      },
      [navigate, onClick]
    )

    return single ? (
      <Button
        role="back-button"
        ref={ref}
        border={false}
        className="h-auto rounded-full bg-border p-2 [&_svg]:size-6"
        onClick={handleClick}
        {...rest}
      >
        <ArrowLeft />
      </Button>
    ) : (
      <Button
        role="back-button"
        ref={ref}
        size="sm"
        outline
        className={cn(
          'font-Orbitron text-sm',
          'mb-4 xl:mb-6 xl:[&_svg]:size-6',
          className
        )}
        onClick={handleClick}
        {...rest}
      >
        <Back />
        {children || t('common.back')}
      </Button>
    )
  }
)
BackButton.displayName = 'BackButton'

// 异步按钮，onClick方法返回Promise则自动设置loading状态
export const AsyncButton = React.forwardRef<
  { cancel?: () => void },
  React.ComponentProps<typeof Button> & {
    loading?: boolean
    connection?: boolean
  }
>(({ children, loading, disabled, className, connection, ...props }, ref) => {
  const { t } = useTranslation()
  const { isConnected } = useSolana()
  const { connect } = useWallet()
  const [loadingStatus, setLoadingStatus] = React.useState(false)
  const refLoadingSign = React.useRef<any>() // 当前loadings事件标识

  React.useImperativeHandle(ref, () => {
    return {
      cancel: cancelLoaing
    }
  })

  // 取消loading状态
  const cancelLoaing = () => {
    refLoadingSign.current = null
    setLoadingStatus(false)
  }

  // 代理onClick事件
  const onClickProxy = (event: any) => {
    if (props.onClick) {
      const sign = Date.now()
      refLoadingSign.current = sign
      const executeResult: any = props.onClick(event)
      if (executeResult?.finally) {
        // onClick是异步函数
        setLoadingStatus(true)
        executeResult.finally(() => {
          if (refLoadingSign.current !== sign) {
            return
          }
          setLoadingStatus(false)
        })
      }
    }
  }

  return (
    <Button
      size="sm"
      border={false}
      {...props}
      type={connection && !isConnected ? 'button' : props.type}
      onClick={connection && !isConnected ? connect : onClickProxy}
      className={cn('h-12', className)}
      disabled={disabled || loadingStatus || loading}
    >
      {connection && !isConnected ? (
        t('common.connectWallet')
      ) : loadingStatus || loading ? (
        <Iconloading className="fill-current xl:!size-6" />
      ) : (
        children
      )}
    </Button>
  )
})
AsyncButton.displayName = 'AsyncButton'

export const RefreshButton = React.forwardRef<
  React.ComponentRef<typeof Button>,
  React.ComponentProps<typeof Button> & {
    loading?: boolean
    text?: React.ReactNode
  }
>(({ className, loading, text, ...props }, ref) => {
  return (
    <Button
      ref={ref}
      outline
      size="sm"
      className={cn('w-9 !px-0 xl:w-12 xl:[&_svg]:size-6', className)}
      {...props}
    >
      <Refresh className={(loading && 'animate-spin-reverse') || ''} />
      {text}
    </Button>
  )
})
RefreshButton.displayName = 'RefreshButton'

export const BeautyButton = React.forwardRef<
  React.ComponentRef<typeof Button>,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    wrapperClassName?: string
    loading?: boolean
    open?: boolean
    selector?: boolean
  }
>(
  (
    {
      wrapperClassName,
      className,
      children,
      loading,
      selector,
      open,
      ...props
    },
    ref
  ) => (
    <Button
      ref={ref}
      size="xs"
      border={false}
      variant="revert"
      className={cn(
        'beauty-button box-content h-8 p-[1px] text-white',
        wrapperClassName
      )}
      {...props}
    >
      <Flex
        className={cn(
          'beauty-button-content',
          'size-full items-center justify-center gap-1 rounded-sm',
          'px-2',
          '[&_svg]:size-4',
          className
        )}
      >
        {loading && <Iconloading />}
        {children}
        {selector && (
          <ArrowDown
            className={cn(
              'transition-transform',
              open ? 'rotate-180' : 'rotate-0'
            )}
          />
        )}
      </Flex>
    </Button>
  )
)
BeautyButton.displayName = 'BeautyButton'
